import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { DateInput } from "@/components/ui/date-input";
import { X, Check, ChevronsUpDown, UserPlus, UserMinus } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import type { Client, Trip } from "@/lib/shared-schema";
import { insertTripSchema } from "@/lib/shared-schema";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { useEffect, useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import DestinationSelector, { type DestinationOption } from "@/components/DestinationSelector";
import { useResolvedFeatureFlags } from "@/hooks/useResolvedFeatureFlags";

const destinationSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  destinationType: z.enum(["Region", "Country", "US State"]),
});

const buildTripFormSchema = (invoicingMvpEnabled: boolean) => insertTripSchema.extend({
  startDate: z.string().optional().default(""),
  endDate: z.string().optional().default(""),
  countries: z.array(z.string()).optional().default([]),
  destinations: invoicingMvpEnabled
    ? z.array(destinationSchema).min(1, "At least one destination is required").default([])
    : z.array(destinationSchema).optional().default([]),
  isGroup: z.boolean().optional().default(false),
  groupName: z.string().optional().default(""),
  clientId: z.number().optional(),
  clientName: z.string().optional(),
  clientInterests: z.string().optional(),
  contactIds: z.array(z.number()).optional().default([]),
}).refine(
  (data) => {
    // Both dates must be provided for validation
    if (!data.startDate || !data.endDate) {
      return true; // Let required validation handle missing dates
    }

    const start = new Date(data.startDate);
    const end = new Date(data.endDate);

    // End date must be same day or after start date
    return end >= start;
  },
  {
    message: "End date must be after start date",
    path: ["endDate"], // Show error on end date field
  }
).refine(
  (data) => {
    // Check maximum trip duration (365 days)
    if (!data.startDate || !data.endDate) {
      return true;
    }

    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    return daysDiff <= 365;
  },
  {
    message: "Trip duration cannot exceed 365 days",
    path: ["endDate"],
  }
);

type TripFormValues = z.infer<ReturnType<typeof buildTripFormSchema>>;

interface TripFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trip?: Trip;
  onSuccess?: (trip: Trip) => void;
  initialContactId?: number; // Optional initial contact ID when creating from contact page
  compact?: boolean; // When true, shows only core trip fields (name, dates, destinations)
}

export default function TripFormDialog({
  open,
  onOpenChange,
  trip: initialTrip,
  onSuccess,
  initialContactId,
  compact = false,
}: TripFormDialogProps) {
  const { toast } = useToast();
  const { isEnabled: isResolvedEnabled } = useResolvedFeatureFlags();
  const invoicingMvpEnabled = isResolvedEnabled("invoicingMvp");
  const tripFormSchema = useMemo(() => buildTripFormSchema(invoicingMvpEnabled), [invoicingMvpEnabled]);

  // Determine if we're editing or creating
  const isEditing = !!initialTrip;

  // For destination selection (WorldVia normalized destinations)
  const [selectedDestinations, setSelectedDestinations] = useState<DestinationOption[]>([]);

  // Group trip state
  const [isGroupTrip, setIsGroupTrip] = useState<boolean>(!!initialTrip?.isGroup);

  // State for contact selection
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [primaryContactId, setPrimaryContactId] = useState<number | null>(null);
  const [isContactPopoverOpen, setIsContactPopoverOpen] = useState(false);
  const [contactSearch, setContactSearch] = useState("");

  // State for client autocomplete
  const [selectedClientId, setSelectedClientId] = useState<number | null>(initialTrip?.clientId || null);
  const [isClientPopoverOpen, setIsClientPopoverOpen] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  
  // Fetch all contacts once when dialog opens - simple and reliable
  const { data: contactsData, isLoading: contactsLoading, error: contactsError } = useQuery<Client[]>({
    queryKey: ["/api/contacts"],
    enabled: open && !compact,
    staleTime: 5 * 60 * 1000, // 5 minutes - reasonable for contact list
    queryFn: async () => {
      const res = await fetch("/api/contacts?limit=1000", {
        credentials: "include"
      }); // Get all contacts
      if (!res.ok) {
        throw new Error(`Failed to fetch contacts: ${res.status}`);
      }
      const json = await res.json();
      // Handle different response formats
      if (Array.isArray(json)) {
        return json;
      }
      if (Array.isArray(json.data)) {
        return json.data;
      }
      if (Array.isArray(json.contacts)) {
        return json.contacts;
      }
      return [];
    }
  });
  
  // Client-side filtering - simple and fast
  const allContacts = contactsData || [];
  const filteredContacts = contactSearch.trim() 
    ? allContacts.filter(contact => {
        const searchTerm = contactSearch.toLowerCase();
        const fullName = `${contact.firstName} ${contact.lastName}`.toLowerCase();
        const email = (contact.email || '').toLowerCase();
        return fullName.includes(searchTerm) || email.includes(searchTerm);
      })
    : allContacts;
  
  // Fetch contacts for existing trip
  const { data: tripContactsData } = useQuery<Client[]>({
    queryKey: initialTrip ? [`/api/trips/${initialTrip.id}/contacts`] : [],
    enabled: !!initialTrip && open && !compact,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Memoize tripContacts to prevent unnecessary re-renders when creating a new trip
  // Without this, the default empty array creates a new reference on each render,
  // causing form.reset() to trigger continuously and clear user input
  const tripContacts = useMemo(() => tripContactsData || [], [tripContactsData]);

  // Fetch existing trip destinations when editing (only when invoicingMvp enabled)
  const { data: tripDestinationsData } = useQuery<DestinationOption[]>({
    queryKey: initialTrip ? [`/api/destinations/trip/${initialTrip.id}`] : [],
    enabled: invoicingMvpEnabled && !!initialTrip && open,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!initialTrip) return [];
      const res = await fetch(`/api/destinations/trip/${initialTrip.id}`, { credentials: "include" });
      if (!res.ok) return [];
      const rows = await res.json();
      return rows.map((r: any) => ({
        id: r.worldviaDestinationId,
        displayName: r.displayName,
        destinationType: r.destinationType,
      }));
    },
  });

  const tripDestinations = useMemo(() => {
    if (tripDestinationsData && tripDestinationsData.length > 0) {
      return tripDestinationsData;
    }
    const legacy = initialTrip?.countries;
    if (initialTrip && Array.isArray(legacy) && legacy.length > 0) {
      return (legacy as string[]).map((name) => ({
        id: `legacy-${name}`,
        displayName: name,
        destinationType: "Country" as const,
      }));
    }
    return [];
  }, [tripDestinationsData, initialTrip]);

  // Fetch clients for autocomplete (same endpoint as contacts)
  const { data: clientsData } = useQuery<Client[]>({
    queryKey: ["/api/contacts", "clients"],
    enabled: open && !compact,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await fetch("/api/contacts?limit=1000", {
        credentials: "include"
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch clients: ${res.status}`);
      }
      const json = await res.json();
      if (Array.isArray(json)) return json;
      if (Array.isArray(json.data)) return json.data;
      if (Array.isArray(json.contacts)) return json.contacts;
      return [];
    }
  });

  // Filter clients for autocomplete
  const allClients = clientsData || [];
  const filteredClients = clientSearchQuery.trim()
    ? allClients.filter(client => {
        const searchTerm = clientSearchQuery.toLowerCase();
        const fullName = `${client.firstName} ${client.lastName}`.toLowerCase();
        const email = (client.email || '').toLowerCase();
        return fullName.includes(searchTerm) || email.includes(searchTerm);
      })
    : allClients;
  
  // Initialize selected contacts from trip or initial contact
  useEffect(() => {
    if (open && !compact) {
      if (isEditing && tripContacts.length > 0) {
        setSelectedContactIds(tripContacts.map(c => c.id));
        // Find the primary contact from the trip contacts
        // We need to fetch the contact-trip associations to get the roles
        if (initialTrip) {
          fetch(`/api/trips/${initialTrip.id}/contact-trips`)
            .then(res => res.json())
            .then(associations => {
              const primaryAssociation = associations.find((assoc: any) => assoc.role === "primary");
              if (primaryAssociation) {
                setPrimaryContactId(primaryAssociation.contactId);
              } else if (tripContacts.length > 0) {
                // If no primary is set, make the first contact primary
                setPrimaryContactId(tripContacts[0].id);
              }
            })
            .catch(err => {
              console.error("Error loading contact associations:", err);
              // Fallback: make first contact primary
              if (tripContacts.length > 0) {
                setPrimaryContactId(tripContacts[0].id);
              }
            });
        }
      } else if (initialContactId) {
        setSelectedContactIds([initialContactId]);
        setPrimaryContactId(initialContactId); // Make the initial contact primary
      } else {
        setSelectedContactIds([]);
        setPrimaryContactId(null);
      }
    }
  }, [open, isEditing, tripContacts, initialContactId, initialTrip, compact]);
  
  const formResolver = useMemo(() => zodResolver(tripFormSchema), [tripFormSchema]);
  const form = useForm<TripFormValues>({
    resolver: formResolver,
    defaultValues: {
      name: initialTrip?.name || "",
      startDate: initialTrip?.startDate
        ? format(new Date(initialTrip.startDate), "yyyy-MM-dd")
        : "",
      endDate: initialTrip?.endDate
        ? format(new Date(initialTrip.endDate), "yyyy-MM-dd")
        : "",
      countries: [],
      destinations: [],
      isGroup: !!initialTrip?.isGroup,
      groupName: initialTrip?.groupName || "",
      clientId: initialTrip?.clientId || undefined,
      clientName: initialTrip?.clientName || "",
      clientInterests: initialTrip?.clientInterests || "",
      contactIds: initialTrip ? tripContacts.map(c => c.id) : initialContactId ? [initialContactId] : [],
    },
  });

  // Reset the form when initial values change
  useEffect(() => {
    if (open) {
      setSelectedDestinations(tripDestinations);
      setSelectedClientId(initialTrip?.clientId || null);
      setIsGroupTrip(!!initialTrip?.isGroup);
      form.reset({
        name: initialTrip?.name || "",
        startDate: initialTrip?.startDate
          ? format(new Date(initialTrip.startDate), "yyyy-MM-dd")
          : "",
        endDate: initialTrip?.endDate
          ? format(new Date(initialTrip.endDate), "yyyy-MM-dd")
          : "",
        countries: [],
        destinations: tripDestinations,
        isGroup: !!initialTrip?.isGroup,
        groupName: initialTrip?.groupName || "",
        clientId: initialTrip?.clientId || undefined,
        clientName: initialTrip?.clientName || "",
        clientInterests: initialTrip?.clientInterests || "",
        contactIds: initialTrip ? tripContacts.map(c => c.id) : initialContactId ? [initialContactId] : [],
      });
    }
  }, [form, initialTrip, open, tripContacts, tripDestinations, initialContactId, compact]);

  // Prevent Enter key from submitting form prematurely
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
      e.preventDefault();
    }
  };

  const onSubmit = async (data: TripFormValues) => {
    try {
      const countriesFromDestinations = invoicingMvpEnabled
        ? selectedDestinations.map((d) => d.displayName)
        : (data.countries || []);

      const tripData: Record<string, unknown> = {
        ...data,
        countries: countriesFromDestinations,
        destinations: undefined, // Don't send to trip endpoint
        isGroup: isGroupTrip,
        groupName: isGroupTrip ? data.groupName : null,
        clientId: selectedClientId || undefined,
      };

      if (invoicingMvpEnabled && isEditing) {
        delete tripData.startDate;
        delete tripData.endDate;
      }

      let trip: Trip;

      if (isEditing && initialTrip) {
        trip = await apiRequest("PATCH", `/api/trips/${initialTrip.id}`, tripData);
      } else {
        trip = await apiRequest("POST", "/api/trips", tripData);
      }

      let destinationSyncFailed = false;
      if (invoicingMvpEnabled) {
        try {
          const syncRes = await fetch(`/api/destinations/trip/${trip.id}/sync`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ destinations: selectedDestinations }),
          });
          if (!syncRes.ok) {
            const body = await syncRes.json().catch(() => ({}));
            console.error("Destination sync failed:", body.message || syncRes.status);
            destinationSyncFailed = true;
          }
        } catch (syncErr) {
          console.error("Destination sync error:", syncErr);
          destinationSyncFailed = true;
        }
      }

      // Handle contact associations
      if (!compact && selectedContactIds.length > 0) {
        // First, get existing contact associations for this trip
        const existingAssociations = isEditing
          ? await apiRequest("GET", `/api/trips/${trip.id}/contacts`)
          : [];

        // Get all contact-trip associations for this trip (needed for role updates and deletions)
        const tripContactAssociations = isEditing
          ? await apiRequest("GET", `/api/trips/${trip.id}/contact-trips`)
          : [];

        // Determine which contacts to add and which to remove
        const existingContactIds = existingAssociations.map((c: Client) => c.id);
        const contactsToAdd = selectedContactIds.filter((id: number) => !existingContactIds.includes(id));
        const contactsToRemove = existingContactIds.filter((id: number) => !selectedContactIds.includes(id));

        // Add new contact associations
        for (const contactId of contactsToAdd) {
          const role = contactId === primaryContactId ? "primary" : "traveler";
          await apiRequest("POST", "/api/contact-trips", {
            contactId,
            tripId: trip.id,
            role: role
          });
        }

        // Update existing contact associations if primary contact changed
        if (isEditing) {
          for (const contactId of selectedContactIds) {
            if (!contactsToAdd.includes(contactId)) {
              // This is an existing contact, check if role needs updating
              const association = tripContactAssociations.find((a: any) => a.contactId === contactId);
              if (association) {
                const newRole = contactId === primaryContactId ? "primary" : "traveler";
                if (association.role !== newRole) {
                  await apiRequest("PATCH", `/api/contact-trips/${association.id}`, {
                    role: newRole
                  });
                }
              }
            }
          }
        }

        // Remove contact associations
        for (const contactId of contactsToRemove) {
          // Find the association in the trip contact associations we already fetched
          const association = isEditing
            ? tripContactAssociations.find((a: any) => a.contactId === contactId)
            : null;
          if (association) {
            await apiRequest("DELETE", `/api/contact-trips/${association.id}`);
          }
        }
      }

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ["/api/trips"] });
      if (isEditing) {
        queryClient.invalidateQueries({ queryKey: [`/api/trips/${trip.id}`] });
        queryClient.invalidateQueries({ queryKey: [`/api/trips/${trip.id}/contacts`] });
        queryClient.invalidateQueries({ queryKey: [`/api/trips/${trip.id}/contact-trips`] });
      }

      toast({
        title: isEditing ? "Trip updated" : "Trip created",
        description: destinationSyncFailed
          ? "Trip saved, but destination sync failed. You can re-add destinations from the trip page."
          : isEditing
            ? "Your trip has been updated successfully."
            : "Your new trip has been created successfully.",
        variant: destinationSyncFailed ? "destructive" : "default",
      });

      onOpenChange(false);
      if (onSuccess) {
        onSuccess(trip);
      }
    } catch (error) {
      console.error("Error saving trip:", error);
      toast({
        title: "Error",
        description: isEditing
          ? "Failed to update trip. Please try again."
          : "Failed to create trip. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(compact ? "max-w-xl" : "max-w-4xl", "max-h-[90vh] overflow-hidden flex flex-col")}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{isEditing ? "Edit Trip" : "Create New Trip"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the details of your trip."
              : "Fill in the details to create a new trip."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} onKeyDown={handleKeyDown} className="flex-1 flex flex-col overflow-hidden">
            {!compact && (
              <div className="flex-shrink-0 pb-4 border-b">
                <Button type="submit" className="w-full">
                  {isEditing ? "Update Trip" : "Create Trip"}
                </Button>
              </div>
            )}

            {/* Scrollable Content */}
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-6 py-4">
                {/* Trip Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Trip Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter a name for your trip" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Dates */}
                {/* When editing an existing trip under the Invoicing MVP flag,
                 * the server ignores `startDate`/`endDate` on PATCH (see
                 * onSubmit() — both keys are stripped from the payload). Mirror
                 * that constraint in the UI so users can't silently "edit"
                 * dates that won't be saved. */}
                {(() => {
                  const datesLocked = invoicingMvpEnabled && isEditing;
                  return (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="startDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Start Date</FormLabel>
                              <FormControl>
                                <DateInput
                                  value={field.value}
                                  onChange={(value) => {
                                    field.onChange(value);
                                    // Trigger validation on endDate when startDate changes
                                    if (form.getValues("endDate")) {
                                      form.trigger("endDate");
                                    }
                                  }}
                                  minDate={new Date()}
                                  disabled={datesLocked}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="endDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>End Date</FormLabel>
                              <FormControl>
                                <DateInput
                                  value={field.value}
                                  onChange={(value) => {
                                    field.onChange(value);
                                    // Trigger validation immediately when endDate changes
                                    form.trigger("endDate");
                                  }}
                                  minDate={form.getValues("startDate") ? new Date(form.getValues("startDate")) : new Date()}
                                  disabled={datesLocked}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      {datesLocked && (
                        <p className="text-xs text-muted-foreground">
                          Trip dates can&apos;t be edited while Invoicing is enabled — they&apos;re derived from the trip&apos;s invoices and components. Adjust those to shift the date range.
                        </p>
                      )}
                    </div>
                  );
                })()}
                
                {/* Destinations Selection (WorldVia normalized) — gated by invoicingMvp */}
                {invoicingMvpEnabled && (
                  <FormField
                    control={form.control}
                    name="destinations"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Destinations *</FormLabel>
                        <FormControl>
                          <DestinationSelector
                            value={selectedDestinations}
                            onChange={(destinations) => {
                              setSelectedDestinations(destinations);
                              field.onChange(destinations);
                            }}
                            placeholder="Type to search regions, countries, or U.S. states..."
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Group Trip Section — gated by invoicingMvp */}
                {invoicingMvpEnabled && (
                  <div className="border rounded-md p-4 bg-muted/30">
                    <FormField
                      control={form.control}
                      name="isGroup"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-2 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={(checked) => {
                                const val = !!checked;
                                field.onChange(val);
                                setIsGroupTrip(val);
                                if (!val) {
                                  form.setValue("groupName", "");
                                }
                              }}
                            />
                          </FormControl>
                          <FormLabel className="text-sm font-medium leading-none">
                            Group Trip
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                    {isGroupTrip && (
                      <FormField
                        control={form.control}
                        name="groupName"
                        render={({ field }) => (
                          <FormItem className="mt-3">
                            <FormLabel>Group Booking Number</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter group booking number" {...field} value={field.value || ''} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>
                )}

                {/* Client Section — hidden in compact mode */}
                {!compact && <div className="border rounded-md p-4 bg-muted/30">
                  <h3 className="text-sm font-semibold mb-4">Client Information</h3>

                  <div className="space-y-4">
                    {/* Client Autocomplete */}
                    <FormField
                      control={form.control}
                      name="clientId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Select Client</FormLabel>
                          <Popover open={isClientPopoverOpen} onOpenChange={setIsClientPopoverOpen}>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  className={cn(
                                    "w-full justify-between",
                                    !selectedClientId && "text-muted-foreground"
                                  )}
                                >
                                  {selectedClientId
                                    ? (() => {
                                        const client = allClients.find((c) => c.id === selectedClientId);
                                        return client ? `${client.firstName} ${client.lastName}` : "Select client...";
                                      })()
                                    : "Select client..."}
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-[400px] p-0" align="start">
                              <Command>
                                <CommandInput
                                  placeholder="Search clients..."
                                  value={clientSearchQuery}
                                  onValueChange={setClientSearchQuery}
                                />
                                <CommandList>
                                  <CommandEmpty>No clients found.</CommandEmpty>
                                  <CommandGroup>
                                    <ScrollArea className="h-[200px]">
                                      {filteredClients.map((client) => (
                                        <CommandItem
                                          key={client.id}
                                          value={`${client.firstName} ${client.lastName}`}
                                          onSelect={() => {
                                            setSelectedClientId(client.id);
                                            field.onChange(client.id);
                                            // Auto-fill client name if empty
                                            if (!form.getValues("clientName")) {
                                              form.setValue("clientName", `${client.firstName} ${client.lastName}`);
                                            }
                                            setIsClientPopoverOpen(false);
                                          }}
                                        >
                                          <div className="flex items-center gap-2 flex-1">
                                            <Avatar className="h-6 w-6">
                                              <AvatarFallback className="text-xs bg-primary/10">
                                                {client.firstName.charAt(0)}{client.lastName.charAt(0)}
                                              </AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1">
                                              <div className="font-medium">
                                                {client.firstName} {client.lastName}
                                              </div>
                                              {client.email && (
                                                <div className="text-xs text-muted-foreground">
                                                  {client.email}
                                                </div>
                                              )}
                                            </div>
                                            {selectedClientId === client.id && (
                                              <Check className="h-4 w-4 text-primary" />
                                            )}
                                          </div>
                                        </CommandItem>
                                      ))}
                                    </ScrollArea>
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Client Name (editable override) */}
                    <FormField
                      control={form.control}
                      name="clientName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Client Name (optional override)</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter client's name" {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Client Interests */}
                    <FormField
                      control={form.control}
                      name="clientInterests"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Client Interests</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="E.g., hiking, museums, fine dining, photography..."
                              className="resize-none"
                              {...field}
                              value={field.value || ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>}


                {/* Contacts Section — hidden in compact mode */}
                {!compact && <div className="border rounded-md p-4 bg-muted/30">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold">Associated Contacts</h3>
                    <Popover open={isContactPopoverOpen} onOpenChange={setIsContactPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm">
                          <UserPlus className="h-4 w-4 mr-2" />
                          Add Contact
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="end">
                        <Command>
                          <CommandInput 
                            placeholder="Search contacts..." 
                            value={contactSearch}
                            onValueChange={setContactSearch}
                          />
                          <CommandList>
                            <CommandEmpty>
                              {contactsLoading ? "Searching..." : "No contacts found."}
                            </CommandEmpty>
                            <CommandGroup>
                              <ScrollArea className="h-[200px]">
                                {contactsLoading ? (
                                  <div className="flex justify-center items-center py-4">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                                    <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
                                  </div>
                                ) : filteredContacts.length > 0 ? (
                                  filteredContacts.map((contact) => (
                                    <CommandItem
                                      key={contact.id}
                                      onSelect={() => {
                                        if (selectedContactIds.includes(contact.id)) {
                                          setSelectedContactIds((prev: number[]) =>
                                            prev.filter((id: number) => id !== contact.id)
                                          );
                                          // If removing the primary contact, set a new primary
                                          if (contact.id === primaryContactId) {
                                            const remainingContacts = selectedContactIds.filter(id => id !== contact.id);
                                            setPrimaryContactId(remainingContacts.length > 0 ? remainingContacts[0] : null);
                                          }
                                        } else {
                                          setSelectedContactIds((prev: number[]) => [...prev, contact.id]);
                                          // If this is the first contact, make it primary
                                          if (selectedContactIds.length === 0) {
                                            setPrimaryContactId(contact.id);
                                          }
                                        }
                                      }}
                                    >
                                      <div className="flex items-center gap-2">
                                        <Avatar className="h-6 w-6">
                                          <AvatarFallback className="text-xs">
                                            {contact.firstName.charAt(0)}{contact.lastName.charAt(0)}
                                          </AvatarFallback>
                                        </Avatar>
                                        <span>{contact.firstName} {contact.lastName}</span>
                                        {selectedContactIds.includes(contact.id) && (
                                          <Check className="h-4 w-4 ml-auto text-primary" />
                                        )}
                                      </div>
                                    </CommandItem>
                                  ))
                                ) : (
                                  <div className="p-2 text-muted-foreground text-sm text-center">
                                    {contactsError ? "Error loading contacts" : "No contacts found"}
                                  </div>
                                )}
                              </ScrollArea>
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  {selectedContactIds.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No contacts associated with this trip.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-sm text-muted-foreground mb-3">
                        Select a primary contact for this trip:
                      </div>
                      {selectedContactIds.map(contactId => {
                        const contact = allContacts.find(c => c.id === contactId);
                        if (!contact) return null;
                        
                        const isPrimary = contactId === primaryContactId;
                        
                        return (
                          <div 
                            key={contact.id} 
                            className={cn(
                              "flex items-center justify-between p-3 border rounded-md transition-colors",
                              isPrimary ? "border-primary bg-primary/5" : "border-gray-200"
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name="primaryContact"
                                  checked={isPrimary}
                                  onChange={() => setPrimaryContactId(contactId)}
                                  className="h-4 w-4 text-primary"
                                />
                                <Avatar>
                                  <AvatarFallback className={cn(
                                    isPrimary ? "bg-primary text-primary-foreground" : "bg-cyan-100 text-cyan-800"
                                  )}>
                                    {contact.firstName.charAt(0)}{contact.lastName.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{contact.firstName} {contact.lastName}</p>
                                  {isPrimary && (
                                    <Badge variant="default" className="text-xs">
                                      Primary
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {contact.email || "No email"}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => {
                                setSelectedContactIds((prev: number[]) => 
                                  prev.filter((id: number) => id !== contact.id)
                                );
                                // If removing the primary contact, set a new primary
                                if (contactId === primaryContactId) {
                                  const remainingContacts = selectedContactIds.filter(id => id !== contactId);
                                  setPrimaryContactId(remainingContacts.length > 0 ? remainingContacts[0] : null);
                                }
                              }}
                            >
                              <UserMinus className="h-4 w-4" />
                              <span className="sr-only">Remove contact</span>
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>}
              </div>
            </ScrollArea>

            {/* Sticky Create Button at Bottom */}
            <div className="flex-shrink-0 pt-4 border-t mt-auto">
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1">
                  {isEditing ? "Update Trip" : "Create Trip"}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
